import { useState } from "react";
import { Say } from "../widgets/Say";
import { PersistOrtho } from "../ortho/persistOrtho";
import { ConversionRules } from "../ortho/conversionRules";
import { isOrthoRulesError } from "../ortho/serializeOrthoRules";
import { isVersionError } from "../ortho/versionFiles";
import { PhoneLengths } from "../../rules/lengthRules";
import { loadChineseToPinyin } from "../../loader/loadChineseToPinyin";
import { ch_to_pinyin } from "../../transcribe/ch_to_pinyin";

/** test Chinese orthography & tones */
export function Chinese() {
  loadChineseToPinyin();

  return (<>
    <h2>Pinyin (Mandarin)</h2>

    <EnterChinese />

    <ChineseTable name='samples' headers={['', 'rules', 'pinyin', 'tweaked']} samples={chineseSamples} />
    <br />
    <ChineseTable name='initials' headers={['', 'rules', 'pinyin', 'initial', 'alt']} samples={initials} />
    <br />
    <ChineseTable name='finals' headers={['', 'rules', 'pinyin', 'final', 'alt']} samples={finals} />
    <br />
    <ChineseTable name='tone pairs' headers={['', 'rules', 'pinyin', 'pairs', 'alt']} samples={tonePairs} />
    <br />
    <ChineseTable name='ɥ' headers={['', 'rules', 'pinyin']} samples={turnedH} />

    <TestTones />
  </>);
}

function TestTones() {
  return (<>
    <br />
    all tones:
    <ul>
      <li>1: <Say>[jou̯300˩]</Say>, <Say>[[jo8K1u0K1]]</Say></li>
      <li>2: <Say>[jou̯300˨]</Say>, <Say>[[jo8K2u0K2]]</Say></li>
      <li>3: <Say>[jou̯300˧]</Say>, <Say>[[jo8K3u0K3]]</Say></li>
      <li>4: <Say>[jou̯300˦]</Say>, <Say>[[jo8K4u0K4]]</Say></li>
      <li>5: <Say>[jou̯300˥]</Say>, <Say>[[jo8K5u0K5]]</Say></li>
    </ul>
    <ul>
      <li>24: <Say>[jou̯300˨˦]</Say>, <Say>[[jo8K7u0K4]]</Say></li>
      <li>15: <Say>[jou̯300˩˥]</Say>, <Say>[[jo8K9u0K5]]</Say></li>
      <li>51: <Say>[jou̯300˥˩]</Say>, <Say>[[jo8K8u0K1]]</Say></li>
      <li>42: <Say>[jou̯300˦˨]</Say>, <Say>[[jo8K6u0K2]]</Say></li>
    </ul>
    <ul>
      <li>23: <Say>[jou̯300˨˧]</Say>, <Say>[[jo8K+2u0K3]]</Say></li>
      <li>32: <Say>[jou̯300˧˨]</Say>, <Say>[[jo8K+3u0K2]]</Say></li>
      <li>35: <Say>[jou̯300˧˥]</Say>, <Say>[[jo8K+5u0K5]]</Say></li>
    </ul>
    <ul>
      <li>242: <Say>[jou̯300˨˦˨]</Say>, <Say>[[jo8K^u0K2]]</Say></li>
      <li>424: <Say>[jou̯300˦˨˦]</Say>, <Say>[[jo8K#u0K4]]</Say></li>
      <li>214: <Say>[jou̯300˨˩˦]</Say>, <Say>[[jo8K+4u0K4]]</Say></li>
    </ul>
  </>);
}

/** chinese, pinyin, hand-coded IPA if it differs from generated IPA */
const chineseSamples = [
  ['汉语拼音', 'hàn yǔ pīn yīn'],
  ['汉字 ', 'hàn zì'],
  [],

  ['知道', 'zhīdào'],
  ['西儒耳目資', 'Xīrú ěrmù zī', '[ɕi˥ɹu9˨˦ a260˦˨˦ɝ˦mu˥˩ t͡sɨ˥]'],
  ['声母', 'shēngmǔ', '[ʂəŋ˥mu9˨˩˦]'],
  ['韵母', 'yùnmǔ'],
  ['复韵母', 'fùyùnmǔ'],
  ['驴', 'lü'],
  ['炉', 'lu'],
  ['鱼', 'yú'],
  ['你好。', 'Nǐ hǎo'],
  ['你，好吗？', 'Nǐ, hǎo ma?'],
  ['枢纽部分——中央大厅', 'shūniǔ bùfèn — zhōngyāng dàtīng'],
  ['公关', 'gōng-guān', '[k˭ɔ2˥ŋk˭wa4n˥]'],
  ['西安', "Xī'ān"],
  ['先', 'xiān', '[ɕʲɛ˥n]'],

  [],
  ['你是不是迟到了吗？', 'nǐ shì bú shì chí dào le ma?'],
  ['出去', 'chū qù'],
  ['大家一起商量一下。', 'dà jiā yī qǐ shāng liang yī xià.'],
  ['我想买一两自行车。', 'wŏ xiăng măi yī liàng zì xíng chē.'],
  ['', ''],
];
// chinese, pinyin, initial, alt
const initials = [
  ['博', 'bó', 'b', '[p˭wɑ˧˥ | p˭ɔə̯8˧˥]'],
  ['破', 'pò', 'p', '[p˭wɑ˥˩]'],
  ['蘑菇', 'mó gu', 'm', '[mwɑ˧˥ k˭u]'],
  ['佛教', 'fó jiào', 'f'],
  ['德国', 'dé guó', 'd'],
  ['特别', 'tè bié', 't'],
  ['呢', 'ne', 'n', '[lä]'],
  ['冷', 'lěng', 'l'],
  ['哥哥', 'gē ge', 'g', '[k˭ɤ3˥ k˭ə]'],
  ['可以', 'kě yǐ', 'k'],
  ['喝', 'hē', 'h'],
  ['计划', 'jì huà', 'j', '[t͡si2˥˩ xwa4˥˩]'],
  ['七', 'qī', 'q', '[t͡si˥]'],
  ['西', 'xī', 'x', '[si˥]'],
  ['字', 'zì', 'z'],
  ['次', 'cì', 'c'],
  ['四', 'sì', 's'],
  ['只', 'zhǐ', 'zh', '[ʈ͡ʂy3˨˩˦]'],
  ['吃', 'chī', 'ch', '[ʈ͡ʂʰɪ˥]'],
  ['是', 'shì', 'sh', '[ʂɪ˥˩]'],
  ['日', 'rì', 'r'],
  ['五', 'wǔ', 'w'],
  ['医生', 'yī shēng', 'y'],
];
// chinese, pinyin, initial, alt
const finals = [
  ['帮', 'bāng', 'a'],
  ['我', 'wǒ', 'o'],
  ['也', 'yě', 'e'],
  ['爱', 'ài', 'ai'],
  ['累', 'lèi', 'ei'],
  ['要', 'yào', 'ao'],
  ['有', 'yǒu', 'ou'],
  ['安静', 'ān jìng', 'an'],
  ['长', 'cháng', 'ang'],
  ['很', 'hěn', 'en'],
  ['朋友', 'péng you', 'eng'],
  ['送', 'sòng', 'ong'],
  ['儿子', 'ér zi', 'er'],
  ['路', 'lù', 'u'],
  ['话', 'huà', 'ua'],
  ['错', 'cuò', 'uo'],
  ['快', 'kuài', 'uai'],
  ['会', 'huì', 'ui'],
  ['穿', 'chuān', 'uan'],
  ['撞', 'zhuàng', 'uang'],
  ['春天', 'chūn tiān', 'un', '[ʈ͡ʂʰu2˥n tʰjɛ˥n]'],
  ['你', 'nǐ', 'i'],
  ['家', 'jiā', 'ia'],
  ['街', 'jiē', 'ie', '[tʰʲɛ˥]'],
  ['鸟', 'niǎo', 'iao'],
  ['九', 'jiǔ', 'iu'],
  ['先', 'xiān', 'ian'],
  ['想', 'xiǎng', 'iang', '[ɕʲa˨˩˦ŋ]'],
  ['金', 'jīn', 'in'],
  ['冰', 'bīng', 'ing'],
  ['炯', 'jiǒng', 'iong', '[t͡ɕʲo3˨˩˦ŋ]'],
  ['去', 'qù', 'ü'],
  ['月', 'yuè', 'üe'],
  ['远', 'yuǎn', 'üan'],
  ['云', 'yún', 'ün', '[ɥy˧˥n]'],
];
// chinese, pinyin, pair, alt
const tonePairs = [
  ['first 5'],
  ['今天', 'jīn tiān', '1+1 (– –)'],
  ['今年', 'jīn nián', '1+2 (– /)'],
  ['屋子', 'wū zǐ', '1+3 (– ∨)'],
  ['出去', 'chū qù', '1+4 (– \\)'],
  ['second 35'],
  ['房间', 'fáng jiān', '2+1 (/ –)'],
  ['符合', 'fú hé', '2+2 (/ /)'],
  ['牛奶', 'niú nǎi', '2+3 (/ ∨)'],
  ['一下', 'yí xià', '2+4 (/ \\)'],
  ['third 214'],
  ['北京', 'běi jīng', '3+1 (∨ –)'],
  ['警察', 'jǐng chá', '3+2 (∨ /)'],
  ['你好', 'nǐ hǎo', '3+3 (/ ∨)', '[ni˧˥ xãõ̯˨˩˦]'],
  ['有趣', 'yǒu qù', '3+4 (∨ \\)'],
  ['fourth 51'],
  ['唱歌', 'chàng gē', '4+1 (\\ –)'],
  ['问题', 'wèn tí', '4+2 (\\ /)'],
  ['汉语', 'hàn yǔ', '4+3 (\\ ∨)'],
  ['动物', 'dòng wù', '4+4 (\\ \\)'],
];
const turnedH = [
  ['囦', 'yuān'],
  ['元', 'yuán'],
  ['远', 'yuǎn'],
  ['噮', 'yuàn'],
];
/*
  ['bopomofo'],
  ['瓶子', 'píngzi', 'pʰi3˦ŋ̩˦zɨ3˩', 'ㄆㄧㄥˊ˙ㄗ'],
  ['', 'bǎikē quánshū', '', 'ㄅㄞˇ ㄎㄜ ㄑㄩㄢˊ ㄕㄨ'],
  ['歌兒', 'gēr', 'ɡɤ˧a9˩ɝ3˩', 'ㄍㄜㄦ'],
  ['哪兒', 'nǎr', 'na7˩˥a˩ɝ̆˩', 'ㄋㄚˇㄦ'],
  ['點兒', 'yīdiǎnér', '', 'ㄧㄉㄧㄢˇㄦ'],
  ['好玩兒', 'hǎowánr', '', 'ㄏㄠˇㄨㄢˊㄦ'],
*/

function ChineseTable(props: { name: string, headers: string[], samples: string[][] }) {
  return (<>
    {props.name}
    <table>
      <thead><tr>
        {props.headers.map((h, i) => <th key={i}>{h}</th>)}
      </tr></thead>
      <tbody>
        {props.samples.map((a, i) => <ChineseRow info={a} voice="Yaoyao" key={i} />)}
      </tbody>
    </table>
  </>);
}

function ChineseRow(props: { info: string[], voice?: string }) {
  if (props.info.length === 0) {
    return <tr><td>&nbsp;</td></tr>;
  } else if (props.info.length === 1) {
    return <tr><td>{props.info[0]}</td><td><hr /></td><td><hr /></td><td><hr /></td></tr>;
  }
  const pinyinIPA = props.info[1] === '' ? '' : pinyinToIpa(props.info[1]);
  const pinyinLengths = getPinyinLengths();
  const tweaked = props.info[2] ?? '';
  return (<tr>
    <td><SystemSay voice={props.voice ?? 'Yaoyao'}>{props.info[0]}</SystemSay></td>
    <td><Say lengthRules={pinyinLengths}>{`[${pinyinIPA}]`}</Say></td>
    <td>{props.info[1]}</td>
    <td>{tweaked.startsWith('[') ? <Say lengthRules={pinyinLengths}>{`${tweaked}`}</Say>
      : tweaked !== '' ? tweaked : <></>}</td>
    {props.info[3] === undefined ? undefined
      : props.info[3].startsWith('[') ? <td><Say lengthRules={pinyinLengths}>{props.info[3]}</Say></td>
        : <td>{props.info[3]}</td>}
  </tr>);
}


/** enter Chinese symbols hear it spoken using system or synth */
function EnterChinese() {
  const [text, setText] = useState('');
  const [pinyin, setPinyin] = useState('');
  const [ipa, setIpa] = useState('');

  function changeText(t: string) {
    setText(t);

    // ch -> pinyin
    let pinyin: string[] = [];
    for (let i = 0; i < t.length; i++) {
      // need to handle two-word UTF-16
      const codepoint = t.codePointAt(i);
      const s = codepoint !== undefined && codepoint > 0xffff ? t.substring(i, i + 2) : t[i];
      const convert = ch_to_pinyin[s];
      if (convert !== undefined) {
        pinyin.push(convert);
      }
      if (codepoint !== undefined && codepoint > 0xffff) {
        i++;
      }
    }
    const allPinyin = pinyin.join(' ');
    setPinyin(allPinyin);

    // pinyin -> ipa
    const allIpa = pinyinToIpa(allPinyin);
    setIpa(allIpa);
  }

  return (<>
    lookup: <input type="text" size={30} value={text} onChange={(e) => changeText(e.target.value)} />
    <br />
    system speech: <SystemSay voice="Yaoyao">{text}</SystemSay>
    <br />
    pinyin: {pinyin}
    <br />
    ipa: <Say lengthRules={getPinyinLengths()}>{`[${ipa}]`}</Say>
    <br /><br />

  </>);
}


let allSystemVoices: SpeechSynthesisVoice[] | null = null;
function getSystemVoices(): SpeechSynthesisVoice[] | null {
  if (allSystemVoices === null) {
    allSystemVoices = window.speechSynthesis.getVoices();
  }
  return allSystemVoices;
}

/** shows text you can click on to hear it spoken using the system's speech synthesizer */
function SystemSay(props: {
  readonly voice: string, /** the name of one of the installed system voices (can be part of the name) */
  readonly children: string,  /** text to speak */
}) {
  const [voices] = useState(getSystemVoices());
  const [voice] = useState(voices !== null ? voices.filter(v => v.name.includes(props.voice))[0] : null);

  function systemSpeech() {
    if (voice === null) {
      console.log(`${props.voice} not found`);
      return;
    }
    const synth = window.speechSynthesis;
    const utter = new SpeechSynthesisUtterance(props.children);
    utter.voice = voice;
    synth.speak(utter);
  }

  const style: React.CSSProperties = { color: 'darkslateblue' };

  const playButton = '\u23f5';
  return (<span onClick={systemSpeech} className='say' style={style}>
    {playButton}
    {props.children}
  </span>);
}

/** convert pinyin to IPA using cached rules from local storage */
function pinyinToIpa(pinyin: string): string {
  const conversion = getConverter();
  return conversion !== null ? conversion.convert(pinyin) : '<error>';
}
function getPinyinLengths(): PhoneLengths | undefined {
  const conversion = getConverter();
  return conversion?.getAddPhonemeLength();
}

/** load pinyin rules from local storage, compile them, & cache the results */
function getConverter(): ConversionRules | null {
  if (converter === 'error') {
    return null;
  } else if (converter === 'notloaded') {
    const persist = new PersistOrtho();
    const rules = persist.loadRules('pinyin');
    if (rules === null || isOrthoRulesError(rules) || isVersionError(rules)) {
      converter = 'error';
      return null;
    }
    converter = new ConversionRules();
    converter.setRuleSet(rules);
  }
  return converter;
}
let converter: ConversionRules | 'notloaded' | 'error' = 'notloaded';
